import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import apiClient from '../services/api';

interface Feature {
  id: string;
  number: number;
  feature_name: string;
  feature_description: string;
  keywords: string;
  included_in_search: boolean;
  description_expanded: boolean;
  keywords_expanded: boolean;
  source: 'llm_generated' | 'user_created' | 'user_edited';
  created_at: string;
  last_modified_at: string;
}

interface FeaturesListState {
  features: Feature[];
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  subProjectId: string | null;
  version: number;
  isDirty: boolean;
  lastSavedAt: string | null;

  initializeFeaturesList: (subProjectId: string, features?: Feature[]) => void;
  loadFeaturesList: (subProjectId: string) => Promise<void>;
  saveFeaturesList: () => Promise<boolean>;
  generateFeaturesList: () => Promise<void>;

  addFeature: (feature: Partial<Feature>) => void;
  updateFeature: (featureId: string, updates: Partial<Feature>) => void;
  deleteFeature: (featureId: string) => void;

  toggleFeatureIncluded: (featureId: string) => void;
  toggleDescriptionExpanded: (featureId: string) => void;
  toggleKeywordsExpanded: (featureId: string) => void;
  expandAllDescriptions: () => void;
  collapseAllDescriptions: () => void;
  expandAllKeywords: () => void;
  collapseAllKeywords: () => void;

  selectAllFeatures: () => void;
  deselectAllFeatures: () => void;

  reorderFeatures: (startIndex: number, endIndex: number) => void;
  resetFeaturesList: () => void;
}

const useFeaturesListStore = create<FeaturesListState>()(
  devtools(
    persist(
      (set, get) => ({
        features: [],
        isLoading: false,
        isGenerating: false,
        error: null,
        subProjectId: null,
        version: 1,
        isDirty: false,
        lastSavedAt: null,

        initializeFeaturesList: (subProjectId: string, features: Feature[] = []) =>
          set({
            subProjectId,
            features,
            isDirty: false,
            lastSavedAt: new Date().toISOString(),
          }),

        loadFeaturesList: async (subProjectId: string) => {
          set({ isLoading: true, error: null });

          try {
            const response = await apiClient.get(`/sub-projects/${subProjectId}/features-list`);
            const data = response.data;

            set({
              subProjectId,
              features: data.features || [],
              version: data.version || 1,
              lastSavedAt: data.last_saved_at,
              isLoading: false,
              isDirty: false,
            });
          } catch (error) {
            console.error('Error loading FeaturesList:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to load FeaturesList'
            });
          }
        },

        saveFeaturesList: async () => {
          set({ isLoading: true, error: null });

          try {
            const { subProjectId, features, version } = get();

            if (!subProjectId) {
              throw new Error('No sub-project ID specified');
            }

            const response = await apiClient.put(
              `/sub-projects/${subProjectId}/features-list`,
              {
                features,
                version,
              }
            );

            set({
              isLoading: false,
              isDirty: false,
              version: response.data.version,
              lastSavedAt: response.data.last_saved_at,
            });

            return true;
          } catch (error: unknown) {
            console.error('Error saving FeaturesList:', error);
            const e = error as { response?: { status?: number } };
            if (e.response?.status === 409) {
              set({
                isLoading: false,
                error: 'The FeaturesList was modified by another user. Please refresh and try again.'
              });
            } else {
              set({
                isLoading: false,
                error: (error as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to save FeaturesList'
              });
            }

            return false;
          }
        },

        generateFeaturesList: async () => {
          set({ isGenerating: true, error: null });

          try {
            const { subProjectId } = get();

            if (!subProjectId) {
              throw new Error('No sub-project ID specified');
            }

            const response = await apiClient.post(
              `/sub-projects/${subProjectId}/features-list/generate`
            );

            const data = response.data;

            set({
              features: data.features || [],
              version: data.version || 1,
              lastSavedAt: data.last_saved_at,
              isGenerating: false,
              isDirty: false,
            });
          } catch (error) {
            console.error('Error generating FeaturesList:', error);
            set({
              isGenerating: false,
              error: error instanceof Error ? error.message : 'Failed to generate FeaturesList'
            });
          }
        },

        addFeature: (feature: Partial<Feature>) =>
          set((state) => {
            const newFeature: Feature = {
              id: feature.id || crypto.randomUUID(),
              number: state.features.length + 1,
              feature_name: feature.feature_name || '',
              feature_description: feature.feature_description || '',
              keywords: feature.keywords || '',
              included_in_search: feature.included_in_search ?? true,
              description_expanded: false,
              keywords_expanded: false,
              source: feature.source || 'user_created',
              created_at: new Date().toISOString(),
              last_modified_at: new Date().toISOString(),
            };

            return {
              features: [...state.features, newFeature],
              isDirty: true,
            };
          }),

        updateFeature: (featureId: string, updates: Partial<Feature>) =>
          set((state) => ({
            features: state.features.map((f) =>
              f.id === featureId
                ? {
                    ...f,
                    ...updates,
                    source: (updates.feature_name || updates.feature_description || updates.keywords)
                      ? 'user_edited' as const
                      : f.source,
                    last_modified_at: new Date().toISOString(),
                  }
                : f
            ),
            isDirty: true,
          })),

        deleteFeature: (featureId: string) =>
          set((state) => {
            const filtered = state.features.filter((f) => f.id !== featureId);
            const renumbered = filtered.map((f, idx) => ({
              ...f,
              number: idx + 1,
            }));

            return { features: renumbered, isDirty: true };
          }),

        toggleFeatureIncluded: (featureId: string) =>
          set((state) => ({
            features: state.features.map((f) =>
              f.id === featureId
                ? { ...f, included_in_search: !f.included_in_search }
                : f
            ),
            isDirty: true,
          })),

        toggleDescriptionExpanded: (featureId: string) =>
          set((state) => ({
            features: state.features.map((f) =>
              f.id === featureId
                ? { ...f, description_expanded: !f.description_expanded }
                : f
            ),
          })),

        toggleKeywordsExpanded: (featureId: string) =>
          set((state) => ({
            features: state.features.map((f) =>
              f.id === featureId
                ? { ...f, keywords_expanded: !f.keywords_expanded }
                : f
            ),
          })),

        expandAllDescriptions: () =>
          set((state) => ({
            features: state.features.map((f) => ({ ...f, description_expanded: true })),
          })),

        collapseAllDescriptions: () =>
          set((state) => ({
            features: state.features.map((f) => ({ ...f, description_expanded: false })),
          })),

        expandAllKeywords: () =>
          set((state) => ({
            features: state.features.map((f) => ({ ...f, keywords_expanded: true })),
          })),

        collapseAllKeywords: () =>
          set((state) => ({
            features: state.features.map((f) => ({ ...f, keywords_expanded: false })),
          })),

        selectAllFeatures: () =>
          set((state) => ({
            features: state.features.map((f) => ({ ...f, included_in_search: true })),
            isDirty: true,
          })),

        deselectAllFeatures: () =>
          set((state) => ({
            features: state.features.map((f) => ({ ...f, included_in_search: false })),
            isDirty: true,
          })),

        reorderFeatures: (startIndex: number, endIndex: number) =>
          set((state) => {
            const result = Array.from(state.features);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);

            const renumbered = result.map((f, idx) => ({
              ...f,
              number: idx + 1,
            }));

            return { features: renumbered, isDirty: true };
          }),

        resetFeaturesList: () =>
          set({
            features: [],
            isDirty: false,
            error: null,
            version: 1,
          }),
      }),
      {
        name: 'features-list-storage',
        partialize: (state) => ({
          features: state.features,
          subProjectId: state.subProjectId,
          version: state.version,
        }),
      }
    )
  )
);

export default useFeaturesListStore;
export type { Feature, FeaturesListState };
